/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import {
  Grid,
  GridCell,
  Input,
  PageSection,
  Spacer,
  Stack,
  Icon,
  TextSubtitle,
  TextBody,
  RadioGroup,
  Radio,
  SelectOption,
  Select,
  TextCaption,
  TextTitle,
  Snackbar,
  Button,
  Tooltip,
  Slider,
  Avatar,
  PortalDestination,
  sendToast,
} from '@pypestream/design-system';
import { useProStudioRoutes } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import { capitalize, kebabCase } from 'lodash';
import { encodeToBase64 } from '../../utils';
import { sendProStudioEvent } from '../../xstate/app.xstate';
import { useVoiceSelectorModal } from '../modals';
import {
  TwilioConfigInput,
  VoiceAiProvider,
} from '@pypestream/api-services/urql';

// Speech model options by provider
const GOOGLE_SPEECH_MODELS = [
  { value: 'long', label: 'default (long)' },
  { value: 'short', label: 'short' },
] as const;

const DEEPGRAM_SPEECH_MODELS = [
  { value: 'nova-2-atc', label: 'nova-2-atc' },
  { value: 'nova-2-automotive', label: 'nova-2-automotive' },
  { value: 'nova-2-conversationalai', label: 'nova-2-conversationalai' },
  { value: 'nova-2-drivethru', label: 'nova-2-drivethru' },
  { value: 'nova-2-ea', label: 'nova-2-ea' },
  { value: 'nova-2-finance', label: 'nova-2-finance' },
  { value: 'nova-2-general', label: 'nova-2-general' },
  { value: 'nova-2-medical', label: 'nova-2-medical' },
  { value: 'nova-2-meeting', label: 'nova-2-meeting' },
  { value: 'nova-2-phonecall', label: 'nova-2-phonecall' },
  { value: 'nova-2-video', label: 'nova-2-video' },
  { value: 'nova-2-voicemail', label: 'nova-2-voicemail' },
  { value: 'nova-3-general', label: 'nova-3-general' },
  { value: 'nova-3-medical', label: 'nova-3-medical' },
] as const;

const DEFAULT_GOOGLE_SPEECH_MODEL = GOOGLE_SPEECH_MODELS[0].value;
const DEFAULT_DEEPGRAM_SPEECH_MODEL = 'nova-2-general';

// Form types for voice channel settings
export type VoiceChannelFormValues = {
  botName: string;
  language: string;
  env: 'sandbox' | 'live';

  // provider: 'google' | 'eleven-labs' | 'amazon';
  // audioModel: string;
  // speed: number;
  // stability: number;
  // similarity: number;

  ttsProvider: string;
  elevenlabsAudioModel: string;
  elevenlabsVoiceId: string;
  elevenlabsSimilarity: number;
  elevenlabsSpeed: number;
  elevenlabsStability: number;
  elevenlabsVoicePreviewUrl: string;
  elevenlabsVoiceMetadata?: VoiceAiProvider;

  speechModel: string;
  transcriptionProvider: string;

  // phoneNumberName: string;
  accountSid: string;
  authToken: string;
  phoneNumber: string;
  webhookUrl: string;
};

interface ChannelSettingsFormProps {
  type: 'create' | 'update';
  initialValues: Partial<VoiceChannelFormValues>;
  channelId?: string;
}

export const VoiceChannelSettingsForm: FC<ChannelSettingsFormProps> = ({
  type,
  initialValues,
  channelId,
}) => {
  const isUpdating = type === 'update';
  const routes = useProStudioRoutes();
  const navigate = useNavigate();

  const form = useForm<VoiceChannelFormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUseNativeValidation: false,
    criteriaMode: 'all',
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isDirty },
  } = form;

  // Reset form when initialValues change (e.g., when switching from create to edit mode)
  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  // Watch form values for real-time updates
  const watchedValues = watch();

  const { openModal, voiceSelectorModal } = useVoiceSelectorModal({
    selectedValues: {
      provider: watchedValues.ttsProvider,
      audioModel: watchedValues.elevenlabsAudioModel,
      elevenlabsVoiceId: watchedValues.elevenlabsVoiceId,
      elevenlabsSimilarity: watchedValues.elevenlabsSimilarity,
      elevenlabsSpeed: watchedValues.elevenlabsSpeed,
      elevenlabsStability: watchedValues.elevenlabsStability,
    },
    onVoiceSelect: (voiceId, voiceMetadata) => {
      setValue('elevenlabsVoiceId', voiceId, { shouldDirty: true });
      setValue('elevenlabsVoiceMetadata', voiceMetadata as VoiceAiProvider, {
        shouldDirty: true,
      });
    },
  });

  const createVoiceAiChannel = (data: VoiceChannelFormValues) => {
    const { botName, env, ...formData } = data;

    // Encode elevenlabsVoiceMetadata to base64 before sending to backend
    const encodedMetadata = formData.elevenlabsVoiceMetadata
      ? encodeToBase64(JSON.stringify(formData.elevenlabsVoiceMetadata))
      : null;

    // Map form data to TwilioConfigInput structure
    const voiceAiConfig: TwilioConfigInput = {
      accountSid: formData.accountSid,
      authToken: formData.authToken,
      language: formData.language,
      phoneNumber: formData.phoneNumber,
      speechModel: formData.speechModel,
      transcriptionProvider: formData.transcriptionProvider,
      ttsProvider: formData.ttsProvider,
      elevenlabsAudioModel: formData.elevenlabsAudioModel,
      elevenlabsVoiceId: formData.elevenlabsVoiceId,
      elevenlabsSimilarity: String(formData.elevenlabsSimilarity),
      elevenlabsSpeed: String(formData.elevenlabsSpeed),
      elevenlabsStability: String(formData.elevenlabsStability),
      elevenlabsVoiceMetadataB64: encodedMetadata,
    };

    sendProStudioEvent({
      type: 'createChannel',
      payload: {
        type: 'twilio_voice',
        pypeId: '',
        streamId: '',
        name: botName,
        env: env.toLowerCase() as 'live' | 'sandbox',
        voiceAiConfig: voiceAiConfig,
        cb: () => {
          navigate(routes.channels.path);
          form.reset();
        },
      },
    });
  };

  const updateVoiceAiChannel = (data: VoiceChannelFormValues) => {
    const { botName, env, ...formData } = data;

    // Encode elevenlabsVoiceMetadata to base64 before sending to backend
    const encodedMetadata = formData.elevenlabsVoiceMetadata
      ? encodeToBase64(JSON.stringify(formData.elevenlabsVoiceMetadata))
      : null;

    // Map form data to TwilioConfigInput structure
    const voiceAiConfig = {
      accountSid: formData.accountSid,
      authToken: formData.authToken,
      language: formData.language,
      phoneNumber: formData.phoneNumber,
      speechModel: formData.speechModel,
      transcriptionProvider: formData.transcriptionProvider,
      ttsProvider: formData.ttsProvider,
      elevenlabsAudioModel: formData.elevenlabsAudioModel,
      elevenlabsVoiceId: formData.elevenlabsVoiceId,
      elevenlabsSimilarity: String(formData.elevenlabsSimilarity),
      elevenlabsSpeed: String(formData.elevenlabsSpeed),
      elevenlabsStability: String(formData.elevenlabsStability),
      elevenlabsVoiceMetadataB64: encodedMetadata,
    };

    sendProStudioEvent({
      type: 'updateChannel',
      payload: {
        id: channelId || '',
        type: 'twilio_voice',
        pypeId: '',
        streamId: channelId || '',
        name: data.botName,
        env: data.env.toLowerCase() as 'live' | 'sandbox',
        voiceAiConfig: voiceAiConfig,
        cb: () => {
          navigate(routes.channels.path);
          form.reset();
        },
      },
    });
  };

  const handleFormSubmit: SubmitHandler<VoiceChannelFormValues> = (data) => {
    if (isUpdating) {
      updateVoiceAiChannel(data);
    } else {
      createVoiceAiChannel(data);
    }
  };

  const handleCancel = () => {
    form.reset();
  };

  const handleSave = () => {
    handleSubmit(handleFormSubmit)();
  };

  const handleCopyWebhookUrl = async () => {
    if (watchedValues.webhookUrl) {
      try {
        await navigator.clipboard.writeText(watchedValues.webhookUrl);
        sendToast({
          type: 'success',
          text: 'Webhook URL copied to clipboard',
          autoClose: 3000,
        });
      } catch (err) {
        sendToast({
          type: 'error',
          text: 'Failed to copy webhook URL',
          autoClose: 5000,
        });
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <PageSection size="small">
        <Stack
          justifyContent="space-between"
          alignItems="center"
          gutter="small"
        >
          <TextTitle size="xsmall" test-id="ps-channel-details-title">
            Channel Details
          </TextTitle>
          <Stack
            alignItems="center"
            gutter="xsmall"
            test-id="ps-voice-icon-label"
          >
            <Icon name="call" size="small" />
            <TextBody>Voice</TextBody>
          </Stack>
        </Stack>
        <Spacer size="large" />

        <Stack>
          <div style={{ flexGrow: 1 }}>
            <TextBody size="small" test-id="ps-channel-name-label">
              Channel Name
            </TextBody>
            <Spacer size="2xsmall" />
            <Input
              variant="outlined"
              test-id={
                isUpdating
                  ? 'ps-update-channel-name-input'
                  : 'ps-create-channel-name-input'
              }
              placeholder="Channel Name"
              value={watchedValues.botName}
              helpText={errors.botName?.message}
              hasError={!!errors.botName}
              {...register('botName', {
                required: 'Channel name is required',
                minLength: {
                  value: 3,
                  message: 'Channel name must be at least 3 characters',
                },
              })}
              onInput={(e) => {
                // @ts-expect-error - InputWC event target has value property
                const { value } = e.target;
                setValue('botName', value, { shouldDirty: true });
                form.trigger('botName');
              }}
            />
          </div>
          <div style={{ flexGrow: 1 }}>
            <TextBody size="small">Language</TextBody>
            <Spacer size="2xsmall" />
            {/* <Select
              value={watchedValues.language}
              variant="outlined"
              placeholder="Select Language"
              onChange={(e) => {
                setValue('language', e.target.value as string, {
                  shouldDirty: true,
                });
                form.trigger('language');
              }}
            >
              <SelectOption value="eng">English-US</SelectOption>
            </Select> */}
            <Input
              variant="outlined"
              value={watchedValues.language}
              disabled
              placeholder="Select Language"
              onChange={(e) => {
                setValue('language', e.target.value as string, {
                  shouldDirty: true,
                });
              }}
            />
          </div>
        </Stack>
        <Spacer size="large" />
        <Stack gutter="large" alignItems="center">
          <TextSubtitle size="small" test-id="ps-channel-environment-label">
            Environment:
          </TextSubtitle>
          <RadioGroup
            value={watchedValues.env}
            hasError={!!errors.env}
            orientation="horizontal"
            test-id={
              isUpdating
                ? 'ps-update-channel-environment-radio-group'
                : 'ps-create-channel-environment-radio-group'
            }
            onChange={(e) => {
              const env = e.target.value as 'sandbox' | 'live';
              setValue('env', env, { shouldDirty: true });
              form.trigger('env');
            }}
          >
            {['sandbox', 'live'].map((env) => (
              <Radio
                checked={watchedValues.env === env}
                key={env}
                value={env}
                disabled={isUpdating}
              >
                {capitalize(env)}
              </Radio>
            ))}
          </RadioGroup>
        </Stack>
        <Spacer size="2xsmall" />
        <Stack gutter="2xsmall" alignItems="center">
          <Icon name="info" size="xsmall" color="gray" />
          <TextCaption variant="tertiary">
            Cannot be changed after channel is created.
          </TextCaption>
        </Stack>
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-voice-section-title">
          Voice
        </TextTitle>
        <TextBody variant="secondary">
          Choose a provider, voice, and model, then adjust speed, stability, and
          similarity to customize text-to-speech for your solution.
        </TextBody>
        <Spacer size="large" />
        <Stack alignItems="end">
          <div style={{ flexGrow: 1 }}>
            <TextBody size="small">Provider</TextBody>
            <Spacer size="2xsmall" />
            <Select
              value={watchedValues.ttsProvider}
              variant="outlined"
              placeholder="Select Provider"
              test-id="ps-voice-channel-tts-provider-select"
              onChange={(e) => {
                setValue('ttsProvider', e.target.value as string, {
                  shouldDirty: true,
                });
                form.trigger('ttsProvider');
              }}
            >
              {watchedValues.ttsProvider && (
                <Icon
                  name={
                    kebabCase(watchedValues.ttsProvider) as
                      | 'google'
                      | 'eleven-labs'
                      | 'amazon'
                  }
                  size="small"
                  slot="prefix"
                />
              )}
              <SelectOption value="ElevenLabs">
                <Icon name="eleven-labs" size="small" slot="prefix" />
                ElevenLabs
              </SelectOption>
            </Select>
          </div>
          {watchedValues.elevenlabsVoiceMetadata && (
            <div style={{ flexGrow: 1 }}>
              <TextBody size="small">Voice</TextBody>
              <Spacer size="2xsmall" />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 10px',
                  border: '1px solid #dfe1e5',
                  minHeight: '40px',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  gap: '12px',
                  transition: 'border 250ms ease-in-out',
                  cursor: 'pointer',
                }}
              >
                <Avatar
                  label={
                    watchedValues.elevenlabsVoiceMetadata.name
                      ?.substring(0, 2)
                      .toUpperCase() || ''
                  }
                  src={watchedValues.elevenlabsVoiceMetadata.image_url || ''}
                  size="small"
                />
                <div style={{ flex: 1 }}>
                  <TextBody size="small" style={{ fontWeight: 'bold' }}>
                    {watchedValues.elevenlabsVoiceMetadata.name ||
                      'Unknown Voice'}
                  </TextBody>
                </div>
                <Button variant="ghost" size="small" onClick={openModal}>
                  Change
                </Button>
              </div>
            </div>
          )}
          <div style={{ maxWidth: 170, flexGrow: 1 }}>
            <TextBody size="small">Audio Model</TextBody>
            <Spacer size="2xsmall" />
            <Select
              value={watchedValues.elevenlabsAudioModel}
              variant="outlined"
              placeholder="Select Model"
              test-id="ps-voice-channel-audio-model-select"
              disabled={!watchedValues.ttsProvider}
              onChange={(e) => {
                setValue('elevenlabsAudioModel', e.target.value as string, {
                  shouldDirty: true,
                });
                openModal();
              }}
            >
              <SelectOption value="flash_v2_5">flash_v2_5</SelectOption>
              <SelectOption value="flash_v2">flash_v2</SelectOption>
              <SelectOption value="turbo_v2_5">turbo_v2_5</SelectOption>
              <SelectOption value="turbo_v2">turbo_v2</SelectOption>
            </Select>
          </div>
          {/* <IconButton
            name="play"
            size="large"
            variant={isPlaying('voice-preview') ? 'primary' : 'secondary'}
            disabled={!watchedValues.elevenlabsVoicePreviewUrl}
            onClick={() => {
              if (watchedValues.elevenlabsVoicePreviewUrl) {
                playAudio(
                  'voice-preview',
                  watchedValues.elevenlabsVoicePreviewUrl
                );
              }
            }}
          /> */}
        </Stack>
        <Spacer />
        <Stack justifyContent="space-between" alignItems="center">
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Speed</TextBody>
            <Tooltip
              placement="right"
              content="Controls the speed of speech. Higher values make it faster; lower values make it slower."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <TextBody size="small">{watchedValues.elevenlabsSpeed}</TextBody>
        </Stack>
        <Slider
          step={0.1}
          min={0.7}
          max={1.2}
          value={watchedValues.elevenlabsSpeed}
          tooltip="top"
          showMinMaxLabels={false}
          test-id="ps-voice-channel-speed-slider"
          // @ts-expect-error - TODO: fix this
          onInput={(e) => {
            setValue('elevenlabsSpeed', parseFloat(e.target.value), {
              shouldDirty: true,
            });
          }}
        />
        <Spacer />
        <Stack justifyContent="space-between" alignItems="center">
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Stability</TextBody>
            <Tooltip
              placement="right"
              content="Controls the stability of the voice. Higher values make it more stable; lower values make it more variable."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <TextBody size="small">{watchedValues.elevenlabsStability}</TextBody>
        </Stack>
        <Slider
          step={0.1}
          min={0}
          max={1}
          value={watchedValues.elevenlabsStability}
          tooltip="top"
          showMinMaxLabels={false}
          test-id="ps-voice-channel-stability-slider"
          // @ts-expect-error - TODO: fix this
          onInput={(e) => {
            setValue('elevenlabsStability', parseFloat(e.target.value), {
              shouldDirty: true,
            });
          }}
        />
        <Spacer />
        <Stack justifyContent="space-between" alignItems="center">
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Similarity</TextBody>
            <Tooltip
              placement="right"
              content="Controls how similar the voice sounds to the original. Higher values make it more similar; lower values make it more different."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <TextBody size="small">{watchedValues.elevenlabsSimilarity}</TextBody>
        </Stack>
        <Slider
          step={0.1}
          min={0}
          max={1}
          value={watchedValues.elevenlabsSimilarity}
          tooltip="top"
          showMinMaxLabels={false}
          test-id="ps-voice-channel-similarity-slider"
          // @ts-expect-error - TODO: fix this
          onInput={(e) => {
            setValue('elevenlabsSimilarity', parseFloat(e.target.value), {
              shouldDirty: true,
            });
          }}
        />
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-speech-model-title">
          Speech Model
        </TextTitle>
        <TextBody variant="secondary">
          Choose the speech-to-text provider and model for transcribing voice
          input in real time.
        </TextBody>
        <Spacer size="large" />
        <Grid>
          <GridCell xsmall="12" small="6">
            <TextBody size="small">Provider</TextBody>
            <Spacer size="2xsmall" />
            <Select
              variant="outlined"
              value={watchedValues.transcriptionProvider}
              placeholder="Select Provider"
              test-id="ps-voice-channel-stt-provider-select"
              onChange={(e) => {
                const newProvider = e.target.value as 'Google' | 'Deepgram';
                setValue('transcriptionProvider', newProvider, {
                  shouldDirty: true,
                });
                // Reset speechModel to default value for the selected provider
                setValue(
                  'speechModel',
                  newProvider === 'Google'
                    ? DEFAULT_GOOGLE_SPEECH_MODEL
                    : DEFAULT_DEEPGRAM_SPEECH_MODEL,
                  { shouldDirty: true }
                );
                form.trigger('transcriptionProvider');
                form.trigger('speechModel');
              }}
            >
              {watchedValues.transcriptionProvider && (
                <Icon
                  name={
                    watchedValues.transcriptionProvider.toLowerCase() as
                      | 'google'
                      | 'deepgram'
                  }
                  size="small"
                  slot="prefix"
                />
              )}
              <SelectOption value="Google">
                <Icon name="google" size="small" slot="prefix" />
                Google
              </SelectOption>
              <SelectOption value="Deepgram">
                <Icon name="deepgram" size="small" slot="prefix" />
                Deepgram
              </SelectOption>
            </Select>
          </GridCell>
          <GridCell xsmall="12" small="6">
            <TextBody size="small">Speech Model</TextBody>
            <Spacer size="2xsmall" />
            <Select
              variant="outlined"
              value={watchedValues.speechModel}
              placeholder="Select Model"
              test-id="ps-voice-channel-speech-model-select"
              onChange={(e) => {
                setValue('speechModel', e.target.value as string, {
                  shouldDirty: true,
                });
                form.trigger('speechModel');
              }}
            >
              {(watchedValues.transcriptionProvider === 'Google'
                ? GOOGLE_SPEECH_MODELS
                : DEEPGRAM_SPEECH_MODELS
              ).map((model) => (
                <SelectOption key={model.value} value={model.value}>
                  {model.label}
                </SelectOption>
              ))}
            </Select>
          </GridCell>
        </Grid>
      </PageSection>
      <PageSection size="small">
        <TextTitle size="xsmall" test-id="ps-telephony-connection-title">
          Telephony Connection
        </TextTitle>
        <TextBody variant="secondary">
          Please paste the following into your Twillio account to complete the
          Voice AI setup. Reference documentation here.
        </TextBody>
        {/* <Spacer size="large" /> */}
        {/* <TextBody size="small">Phone Number Name</TextBody> */}
        {/* <Spacer size="2xsmall" />
        <Select
          variant="outlined"
          value={watchedValues.phoneNumber}
          onChange={(e) => {
            setValue('phoneNumber', e.target.value as string, {
              shouldDirty: true,
            });
          }}
        >
          <SelectOption value="number1">ADT Twilio Test Number</SelectOption>
          <SelectOption value="number2">ADT Twilio Test Number 2</SelectOption>
          <SelectOption value="number3">ADT Twilio Test Number 3</SelectOption>
        </Select> */}
        <Spacer />
        <TextBody size="small">Twilio Account SID</TextBody>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          value={watchedValues.accountSid}
          helpText={errors.accountSid?.message}
          hasError={!!errors.accountSid}
          test-id="ps-voice-channel-account-sid-input"
          {...register('accountSid', {
            required: 'Twilio Account SID is required',
            // pattern: {
            //   value: /^AC[a-f0-9]{32}$/,
            //   message: 'Invalid Twilio Account SID format',
            // },
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue('accountSid', value, {
              shouldDirty: true,
            });
            form.trigger('accountSid');
          }}
        >
          <Icon name="check" size="small" slot="suffix" />
        </Input>
        <Spacer />
        <TextBody size="small">Twilio Auth Token</TextBody>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          type="password"
          value={watchedValues.authToken}
          helpText={errors.authToken?.message}
          hasError={!!errors.authToken}
          test-id="ps-voice-channel-auth-token-input"
          {...register('authToken', {
            required: 'Twilio Auth Token is required',
            // minLength: {
            //   value: 32,
            //   message: 'Auth token must be at least 32 characters',
            // },
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue('authToken', value, {
              shouldDirty: true,
            });
            form.trigger('authToken');
          }}
        />
        <Spacer />
        <TextBody size="small">Twilio Phone Number</TextBody>
        <Spacer size="2xsmall" />
        <Input
          variant="outlined"
          value={watchedValues.phoneNumber}
          helpText={errors.phoneNumber?.message}
          hasError={!!errors.phoneNumber}
          test-id="ps-voice-channel-phone-number-input"
          {...register('phoneNumber', {
            required: 'Twilio Phone Number is required',
            pattern: {
              value: /^\+[1-9]\d{1,14}$/,
              message: 'Invalid phone number format',
            },
          })}
          onInput={(e) => {
            // @ts-expect-error - InputWC event target has value property
            const { value } = e.target;
            setValue('phoneNumber', value, {
              shouldDirty: true,
            });
            form.trigger('phoneNumber');
          }}
        >
          <Icon name="check" size="small" slot="suffix" />
        </Input>
        <Spacer />
        <TextBody size="small" test-id="ps-webhook-url-label">
          Webhook URL
        </TextBody>
        <Spacer size="2xsmall" />
        {watchedValues.webhookUrl ? (
          <Tooltip placement="top" content="Click to copy webhook URL">
            <div
              onClick={handleCopyWebhookUrl}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '0 10px',
                border: '1px solid #dfe1e5',
                minHeight: '40px',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                gap: '12px',
                transition: 'border 250ms ease-in-out',
                cursor: 'pointer',
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#8573e7';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#dfe1e5';
              }}
            >
              <div style={{ flex: 1, overflow: 'hidden' }}>
                <TextBody
                  size="small"
                  style={{
                    color: '#333',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {watchedValues.webhookUrl}
                </TextBody>
              </div>
              <Icon name="copy" size="small" color="gray" />
            </div>
          </Tooltip>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px',
              border: '1px solid #dfe1e5',
              minHeight: '40px',
              borderRadius: '8px',
              backgroundColor: '#f8f9fa',
              gap: '12px',
            }}
          >
            <TextBody size="small" style={{ color: '#6c757d' }}>
              The Webhook URL will be displayed once you fill out the fields
              above and save changes.
            </TextBody>
          </div>
        )}
        <Spacer />
      </PageSection>
      <PortalDestination name="form-snackbar-destination" />
      <Snackbar id="form-snackbar-destination" open={form.formState.isDirty}>
        There are unsaved changes
        <Button
          variant="secondary"
          size="large"
          onClick={handleCancel}
          slot="action"
          test-id="ps-voice-channel-cancel-button"
        >
          Cancel
        </Button>
        <Button
          size="large"
          onClick={handleSave}
          disabled={!isDirty}
          slot="action"
          test-id="ps-voice-channel-save-button"
        >
          Save Changes
        </Button>
      </Snackbar>

      {voiceSelectorModal}
    </form>
  );
};
